import {assert} from 'Asserts';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  GraphBuilderErrorType,
  GraphBuilderObjectType,
  GraphBuilderValidationError,
} from 'processes/graph-builder/validation/GraphBuilderConstants';
import getLoopIgnoringAncestorsAndEdges from 'util/graph-utils/getLoopIgnoringAncestorsAndEdges';
import getLowestCommonAncestors from 'util/graph-utils/getLowestCommonAncestors';
import {AdjacencyMap} from 'util/graph-utils/GraphUtilConstants';
import isNodeDominated from 'util/graph-utils/isNodeDominated';
import strUtil from 'util/strUtil';

import {
  ProcessFlowchartDesignSpace,
  ProcessFlowchartForValidation,
  ProcessGraphBuilderNode,
} from './ProcessGraphBuilderConstants';
import {
  getFlowchartNodeFromClientId,
  getFulfillingMapperConfigs,
  getInboundEdges,
  getTaskNodesUpstreamOfNode,
  getTemplateNodeFromSchemaId,
} from './ProcessGraphGetters';

/**
 * Computes the lists of nodes that are being merged into the current node.
 *
 *
 * ┌──►B──────┐
 * │          │
 * ├──►C──────┤
 * │          └──►
 * A               G
 * │         ┌───►   ▲
 * ├──►D─────┤       │
 * │         │       │
 * ├──►E─────┘       │
 * │                 │
 * └──►F─────────────┘
 *
 * In the graph above, G has:
 * - five incoming edges (from B, C, D, E, and F)
 * - three incoming mappers (from B&C, D&E, and F)
 *
 * So this function would return [[B, C], [D, E]] because [B, C] and [D, E] represent individual merges into
 * G. F is not returned because it is not part of a merge.
 *
 * @param node The node
 * @param flowchart The flowchart
 * @param designSpace The design space
 * @returns A list of the nodes being merged into `node`.
 */
function getAllMergeSources(
  node: ProcessGraphBuilderNode,
  flowchart: ProcessFlowchartForValidation,
  designSpace: ProcessFlowchartDesignSpace
): Array<Array<ProcessGraphBuilderNode>> | null {
  // The associated template node is guaranteed to exist by ensureNodeIsInsideDesignSpace. But we
  // double-check, in case the tests are out of order
  const templateNode =
    node.type === ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS ||
    node.type === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
      ? getTemplateNodeFromSchemaId(designSpace.availableNodes, node.taskSchemaId)
      : null;
  if (templateNode == null) {
    return null;
  }

  const incomingEdges = getInboundEdges(node, flowchart.edges);
  // The node must have at least two edges to be a merge point.
  if (incomingEdges.length <= 1) {
    return null;
  }

  const fulfillingMapperConfigs = getFulfillingMapperConfigs(node, flowchart, templateNode, incomingEdges);
  if (fulfillingMapperConfigs == null) {
    return null;
  }

  const mergingAncestorsList: Array<Array<ProcessGraphBuilderNode>> = [];
  for (const {fromTaskSchemaIds} of fulfillingMapperConfigs) {
    if (fromTaskSchemaIds.length > 1) {
      const mergingAncestors = [];
      for (const incomingEdge of incomingEdges) {
        const upstreamNode = getFlowchartNodeFromClientId(flowchart, incomingEdge.fromNodeId);
        assert(upstreamNode != null, 'Expected edge to be connected to node', {
          flowchart,
          incomingEdge,
        });
        // Parent nodes should include the task nodes upstream of any directly upstream router node.
        // Currently this can only be one task node.
        const parentNodes =
          upstreamNode.type === ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS ||
          upstreamNode.type === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
            ? [upstreamNode]
            : getTaskNodesUpstreamOfNode(upstreamNode.clientId, flowchart);
        for (const parentNode of parentNodes) {
          if (
            (parentNode.type === ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS ||
              parentNode.type === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS) &&
            fromTaskSchemaIds.includes(parentNode.taskSchemaId)
          ) {
            mergingAncestors.push(parentNode);
          }
        }
      }
      mergingAncestorsList.push(mergingAncestors);
    }
  }
  return mergingAncestorsList;
}

/**
 * Validates that all merge points (aka merging LOYSP) resolve exactly one branch point.
 * While we have all merging nodes and LCAs, we also check that router nodes do not branch out of the merge.
 *
 * In this algorithm a "branch point" is a point in the graph at which the graph "forks" or "diverges".
 * If there is a merging LOYSP we require it to "resolve" (aka merge as we look backward) exactly one branch
 * point. We also require that branch point to be the LCA of the nodes being merged in the LOYSP.
 *
 * **Example 1**
 *
 *     B
 *   /   \
 * A       D
 *   \   /
 *     C
 *
 * In the graph above, A is a "branch point" because the graph is diverging at this point. D is a "merge point"
 * where B and C are being merged into D. The LCA of B and C is A and we can visually see that A is the only
 * branch point being resolved by merging B and C into D. Since the merge point at D resolves only one branch point
 * which is the LCA of the merging nodes, this graph is valid.
 *
 * **Example 2**
 *
 *    B --> C
 *  /  \  /   \
 * A     /      F
 *  \   /  \   /
 *    D -->  E
 *
 * In the graph above, F is a merge point that is merging C and E. But the branch point of C and E is ambiguous.
 * Both B and D are the LCA of C and E. Since the branch point is ambiguous, this graph is invalid.
 *
 * **Example 3**
 *
 *     B --> C
 *   /     /   \
 * A      /     F
 *   \   /     /
 *     D --> E
 *
 * In the graph above, F is the merge point that is merging C and E. However, we can see that while D is the
 * branch point (LCA of C and E), C also depends on B which does not descend from D. This means that the merge
 * of C, E is attempting to resolve two branch points - D and A. This can lead to nonintuitive behavior because
 * of how we are pooling inputs to the merge nodes. It will leave any tasks at C which did not originate from D
 * "orphaned" (eg without a sibling at E) and therefore unmapped. Because of this ambiguity, thsi graph is invalid.
 *
 * So we want to ensure that any merge point has exactly one branch point. This means that
 * 1. All merge points need to have one branch point.
 * 2. Any nodes that are merging into a merge point must be dominated by their least common ancestor.
 *    See: https://en.wikipedia.org/wiki/Dominator_(graph_theory)#Algorithms
 *
 * We are detecting these graph topologies using the following algorithm:
 *
 * For each merge point:
 * 1. Find the nearest branch point. If there are multiple branch points, the graph is invalid.
 * 2. Find all nodes that are ancestors of the merging nodes but are not ancestors of the branch point.
 *    This set represents that nodes that are the dependencies for the merge - in example 3 this would return
 *    B, C, D, and E.
 * 3. Validate that every node from (2) descends from the branch point. If any such node does not descend from
 *    the branch point we raise an error.
 * 4. Validate that for every merging node, there is no router node between the node and the LCA that branches
 *    out of the merge. Also validate that there are no router nodes between the merge point and merging node.
 *
 * @param flowchart The flowchart
 * @param designSpace The design space
 * @returns A list of error objects
 */
export default function ensureMergingNodesOnlyDependOnOneBranchPoint(
  flowchart: ProcessFlowchartForValidation,
  designSpace: ProcessFlowchartDesignSpace
): Array<GraphBuilderValidationError> | null {
  const errors: Array<GraphBuilderValidationError> = [];
  const rootNode = flowchart.nodes.find(
    (node) => node.type === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
  );
  if (rootNode == null) {
    // If the root node doesn't exist we will raise a validation error elsewhere.
    return null;
  }
  const adjacencyMap: AdjacencyMap = {};
  for (const {fromNodeId, toNodeId} of flowchart.edges) {
    if (adjacencyMap[fromNodeId] == null) {
      adjacencyMap[fromNodeId] = [];
    }
    adjacencyMap[fromNodeId].push(toNodeId);
  }

  const {nodeIdsToAncestors, adjacencyMapLoopsRemoved} = getLoopIgnoringAncestorsAndEdges(
    rootNode.clientId,
    adjacencyMap
  );
  for (const node of flowchart.nodes) {
    if (node.type === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS) {
      continue;
    }
    const mergingSourceNodesList = getAllMergeSources(node, flowchart, designSpace);
    if (mergingSourceNodesList == null) {
      continue;
    }
    for (const mergingNodes of mergingSourceNodesList) {
      const mergingLoyspLCAs = getLowestCommonAncestors(
        mergingNodes.map(({clientId}) => clientId),
        nodeIdsToAncestors,
        adjacencyMapLoopsRemoved
      );
      if (mergingLoyspLCAs.length !== 1) {
        errors.push({
          objectId: node.clientId,
          objectType: GraphBuilderObjectType.NODE,
          errorMessage: `${strUtil.getReadableStringFromList(
            mergingNodes.map(({name}) => name).sort((a, b) => a.localeCompare(b))
          )} do not share a clear ancestry. The configuration is invalid because a merge point with an ambiguous branch point has multiple sources in a single transition.`,
          errorType: GraphBuilderErrorType.MERGE_POINT_WITH_AN_AMBIGUOUS_BRANCH_POINT,
        });
        continue;
      }
      const mergingLoyspLCA = mergingLoyspLCAs[0];
      for (const mergingNode of mergingNodes) {
        if (!isNodeDominated(mergingNode.clientId, mergingLoyspLCA, rootNode.clientId, adjacencyMap)) {
          errors.push({
            objectId: mergingNode.clientId,
            objectType: GraphBuilderObjectType.NODE,
            errorMessage:
              'The configuration is invalid because a merge point with an ambiguous branch point has multiple sources in a single transition.',
            errorType: GraphBuilderErrorType.MERGE_POINT_WITH_AN_AMBIGUOUS_BRANCH_POINT,
          });
        }
        const routerNodesBetweenMergingNodeAndLCA = [...nodeIdsToAncestors[mergingNode.clientId]].filter(
          (ancestorNodeClientId) => {
            const ancestorNode = getFlowchartNodeFromClientId(flowchart, ancestorNodeClientId);
            return (
              // Check that router is descendant of LCA
              ancestorNode != null &&
              ancestorNode.type === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS &&
              nodeIdsToAncestors[ancestorNodeClientId].has(mergingLoyspLCA)
            );
          }
        );
        const routerNodesBetweenMergePointAndMergingNode = [...nodeIdsToAncestors[node.clientId]].filter(
          (ancestorNodeClientId) => {
            const ancestorNode = getFlowchartNodeFromClientId(flowchart, ancestorNodeClientId);
            return (
              // Check that router is descendant of merging node
              ancestorNode != null &&
              ancestorNode.type === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS &&
              nodeIdsToAncestors[ancestorNodeClientId].has(mergingNode.clientId)
            );
          }
        );
        for (const routerNodeClientId of routerNodesBetweenMergingNodeAndLCA) {
          const downstreamNodeClientIds = adjacencyMap[routerNodeClientId];
          // There is an error if a node directly downstream of the router leaves the merge.
          // We check this by pushing an error if the downstream node is not an ancestor of mergingNode.
          // We use the merging node rather than the merge point to catch the case where the downstream node leaves the merge but still merges into the merge point.
          if (
            downstreamNodeClientIds.some(
              (downstreamNodeClientId) =>
                !nodeIdsToAncestors[mergingNode.clientId].has(downstreamNodeClientId)
            )
          ) {
            errors.push({
              objectId: routerNodeClientId,
              objectType: GraphBuilderObjectType.NODE,
              errorMessage: `This flowchart is invalid because tasks may not merge into '${node.name}' if they pass through this router.`,
              errorType: GraphBuilderErrorType.MERGE_POINT_WITH_AN_AMBIGUOUS_BRANCH_POINT,
            });
          }
        }
        for (const routerNodeClientId of routerNodesBetweenMergePointAndMergingNode) {
          // For any router nodes between the merge point and merging node, we want to error.
          // We check this separately, as it's not caught by the LCA - merging node check above.
          errors.push({
            objectId: routerNodeClientId,
            objectType: GraphBuilderObjectType.NODE,
            errorMessage: `This flowchart is invalid because tasks may not merge into '${node.name}' if they pass through this router.`,
            errorType: GraphBuilderErrorType.MERGE_POINT_WITH_AN_AMBIGUOUS_BRANCH_POINT,
          });
        }
      }
    }
  }

  return errors;
}
